/* eslint-disable react/jsx-pascal-case */
import React from "react";
import { connect } from "react-redux";

import { Model } from "@flowable/forms";

import Number from "@cayuse/shared-react.components.form.inputs.number";
import Icon from "@cayuse/shared-react.components.icon";
import {
  isNonEmptyArray,
  isNonEmptyObject,
  isNonEmptyString,
} from "@cayuse/shared-react.utils.type-checks";

import BudgetBuilderSummary from "components/flowable/SummaryBudget/BudgetBuilderSummary";
import BudgetInputSelector from "components/flowable/SummaryBudget/BudgetInputSelector";
import SummaryBudgetFooter from "components/flowable/SummaryBudget/SummaryBudgetFooter";
import { BUDGET_ROWS, FLOWABLE_BASE_URL } from "globals/constants";
import enDictionary from "globals/dictionaries/en";
import { loadBudgetBuilder } from "store/actions/budgetBuilder";
import { getBaseRoute } from "utils/helpers";

import componentStyles from "../../../containers/Form/SimplifiedBuilder/Components/SummaryBudgetComponent.css";
import styles from "./SummaryBudget.css";

import { api } from "services";

const uploadDefinition = {
  type: "upload",
  value: "{{file}}",
  extraSettings: {
    autoupload: true,
    acceptFileTypes: ".xlsx,.xls,.xlsm",
    maxFiles: 1,
    downloadUrl: "rest/file/",
    allowDelete: "true",
    preview: "none",
    //fileService: `${FLOWABLE_BASE_URL}/content-api/`,
    fileService: `${FLOWABLE_BASE_URL}/cayuse-api/`,
    uploadUrl: `${FLOWABLE_BASE_URL}/platform-api/content-items/provisional`,
    updateFileUrl: `${FLOWABLE_BASE_URL}/platform-api/content-items//new-version`,
  },
};

const dictionary = enDictionary.summaryBudget;

// TODO: move this to a place it can be easily shared
const toCurrency = (value) => {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

class SummaryBudget extends Model.FormComponent {
  state = {
    confirmInputTypeSwitch: false,
    edit: false,
    inputType: "",
    tempFileData: {},
    uploadId: null,
  };

  constructor(props) {
    super(props);
    let inputType;

    if (this.props.config?.value?.inputType) {
      inputType = this.props.config?.value?.inputType;
    } else {
      inputType = "manual";
    }
    this.state = {
      confirmInputTypeSwitch: false,
      edit: false,
      inputType,
      tempFileData: {},
      uploadId: null,
    };
  }

  componentDidMount() {
    const { inputType } = this.state;
    const { budgetBuilderEnabled, config } = this.props;
    loadBudgetBuilder(true, this.props.payload.submissionType);
    if (inputType === "builder") {
      if (!budgetBuilderEnabled) {
        this.setState({ inputType: "manual" });
        this.onChangeValue({
          ...config.value,
          inputType: "manual",
        });
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return this.state !== nextState || this.props !== nextProps;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { config } = this.props;
    const resolvedValues = config.resolvedUpload.value;
    const hasValues = isNonEmptyArray(resolvedValues);
    const hasId = hasValues && resolvedValues[0].id;

    if (hasValues && hasId && resolvedValues[0].id !== this.state.uploadId) {
      this.setState({ uploadId: resolvedValues[0].id });
      this.uploadFile(resolvedValues);
    } else if (config?.value?.file?.length === 0 && this.state.uploadId) {
      // user removed the file from the upload Component
      this.onChangeValue({
        ...config.value,
        fileData: null,
        uploadUser: null,
      });
      this.setState({ uploadId: null });
    }

    if (this.state.edit && !config.enabled) {
      this.setState({ edit: false });
    }
  }

  uploadFile = (fileInfo) => {
    const { config } = this.props;
    const promises = [
      api.form.getBudget({ id: fileInfo[0].id }),
      api.tenant.getProductUser({ userId: fileInfo[0].lastModifiedBy }),
    ];

    Promise.all(promises).then((results) => {
      this.onChangeValue({ ...config.value, fileData: results[0] });

      if (!config.value?.uploadUser && results[1].user) {
        this.onChangeValue({ ...config.value, uploadUser: results[1].user });
      }
    });
  };

  handleOnChange(event, key) {
    this.setState({
      tempFileData: {
        ...this.state.tempFileData,
        [key]: event.target.value,
      },
    });
  }

  renderBudgetRow(rowName, amount, style, edit, key, isEmpty) {
    const override = this.state.tempFileData[key];
    const value = override !== undefined ? override : (isEmpty ? "" : amount);

    return (
      <tr className={style} key={key}>
        <td>{rowName}</td>
        <td>
          {edit ? (
            <Number
              min="0"
              step="0.01"
              onChange={(event) => this.handleOnChange(event, key)}
              type="number"
              value={value}
            />
          ) : (
            <strong>{isEmpty ? "--" : toCurrency(amount)}</strong>
          )}
        </td>
      </tr>
    );
  }

  renderBudgetRows(parsedData, edit) {
    const formatRows = (data, edit) => {
      const defaultBudget = () => {
        const budget = {};
        Object.keys(BUDGET_ROWS).forEach((key) => {
          budget[key] = "";
        });

        return budget;
      };

      const rows = [];

      data = {
        ...defaultBudget(),
        ...data,
      };

      for (const key in data) {
        const raw = data[key];
        const isEmpty = !isNonEmptyString(raw);
        const amount = isEmpty ? 0 : parseFloat(String(raw).replace(/,/g, ""));

        // render row
        if (BUDGET_ROWS[key]?.type === "expense") {
          rows.push(
            this.renderBudgetRow(
              BUDGET_ROWS[key].value,
              amount,
              componentStyles.cost,
              edit,
              key,
              isEmpty
            )
          );
        } else if (BUDGET_ROWS[key]?.type === "subTotal") {
          rows.push(
            this.renderBudgetRow(
              BUDGET_ROWS[key].value,
              amount,
              componentStyles.total,
              edit,
              key,
              isEmpty
            )
          );
        } else if (BUDGET_ROWS[key]?.type === "total") {
          rows.push(
            this.renderBudgetRow(
              BUDGET_ROWS[key].value,
              amount,
              componentStyles.total,
              edit,
              key,
              isEmpty
            )
          );
        }
      }

      return rows;
    };

    return formatRows(parsedData, edit);
  }

  renderBudgetBuilderLink(budgetBuilderLink) {
    return (
      <>
        <a href={budgetBuilderLink} rel="noopener noreferrer" target="_blank">
          Open budget in Budget Builder
        </a>
        <Icon
          name="external-link-alt"
          className={styles.budgetSummaryHeaderIcon}
        />
      </>
    );
  }

  handleOptionChange = (value, config) => {
    if (this.state.edit) {
      this.setState({ edit: false });
    }

    this.setState({ inputType: value });

    this.onChangeValue({
      ...config.value,
      file: [],
      inputType: value,
    });
  };

  handleManualCancel = () => {
    this.setState({
      edit: !this.state.edit,
      tempFileData: {},
    });
  };

  handleManualSave = () => {
    const hasFileData = isNonEmptyObject(this.props.config?.value?.fileData);
    let newData = {
      ...this.state.tempFileData,
    };

    if (hasFileData) {
      newData = {
        ...this.props.config.value.fileData,
        ...newData,
      };
    }

    this.onChangeValue({
      ...(this.props.config.value ?? {}),
      fileData: newData,
    });
    this.setState({
      edit: !this.state.edit,
      tempFileData: {},
    });
  };

  onChangeValue = (value) => {
    this.dispatchFocusEvent();
    this.props.onChange(value);
  };

  render() {
    const {
      Components,
      config: propsConfig,
      payload,
      budgetBuilderEnabled,
    } = this.props;
    const { inputType } = this.state;
    const defaultInputTypes = dictionary.inputTypes;
    const baseRoute = getBaseRoute(payload.submissionType);
    const formId = payload?.[`${payload.submissionType}Id`];
    const budgetBuilderLink = `${baseRoute}/${formId}/bb`;
    const isBudgetBuilderOption = inputType === defaultInputTypes.builder;

    const config = {
      ...propsConfig,
      enabled: budgetBuilderEnabled && propsConfig.enabled,
    };

    if (!config.resolvedUpload) {
      return null;
    }

    return (
      <div className={styles.container}>
        <BudgetInputSelector
          config={config}
          inputType={inputType}
          onChange={this.handleOptionChange}
        />
        {isBudgetBuilderOption && (
          <div className={styles.budgetSummaryHeader}>
            {this.renderBudgetBuilderLink(budgetBuilderLink)}
          </div>
        )}
        <table className={styles.table}>
          <thead className={`ui form`}>
            <tr className={componentStyles.cost}>
              <td>
                <strong>Budget Item</strong>
              </td>
              <td>
                <strong>Total</strong>
              </td>
            </tr>
          </thead>
          <tbody>
            {isBudgetBuilderOption ? (
              <BudgetBuilderSummary edit={this.state.edit} styles={styles} />
            ) : (
              <>
                {this.renderBudgetRows(config.value?.fileData, this.state.edit)}
              </>
            )}
          </tbody>
        </table>
        <SummaryBudgetFooter
          config={config}
          edit={this.state.edit}
          handleCancel={this.handleManualCancel}
          handleSave={this.handleManualSave}
          inputType={inputType}
          onChange={this.onChangeValue}
          UploadComponent={Components.upload}
          {...this.props}
        />
      </div>
    );
  }

  static $resolve(unresolved, scope, addData, Components) {
    const resolve = Components.panel.$resolve;
    const resolved = resolve(unresolved, scope, addData);
    const disabled = resolved.enabled === false;
    const resolvedUpload = resolve(
      uploadDefinition,
      resolved.value,
      addData,
      disabled
    );

    return { ...resolved, resolvedUpload };
  }
}

const mapStateToProps = (state) => ({
  budgetBuilderEnabled: state.tenant.budgetBuilderEnabled || false,
});

export default connect(mapStateToProps)(SummaryBudget);
